import React, { useState, useEffect } from "react";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import Icon from "../../../components/AppIcon";
import { useConfig } from "../../../context/ConfigContext";
import {
  formatNum,
  formatNumber,
  formatNumberQTZ,
  formatNumKgT,
  formatNumMil,
} from "../../../components/utils/format";
import { roundTo } from "../../../components/utils/mathUtils";

import { useMonthlyImpact } from "../../../hooks/useMonthlyImpact";

const ImpactDashboard = () => {
  const config = useConfig();
  const {
    CO2_TOTAL,
    TOTAL_ARBOLES,
    CARS_OFF_TOTAL,
    ENERGIA_TOTAL,
    CARBON_TOTAL,
    AHORRO_TOTAL,
    FACT_CO2_PROMEDIO_ANUAL,
    FACT_ARBOLES_PROM_ANUAL,
  } = config;

  const {
    availableYears,
    getDataByYear,
    loading: loadingData,
    hasData,
  } = useMonthlyImpact();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showSavings, setShowSavings] = useState(true);

  // Auto-select most recent year if current year has no data
  useEffect(() => {
    if (availableYears.length > 0 && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears]);

  const [animatedValues, setAnimatedValues] = useState({
    co2Reduced: 0,
    treesEquivalent: 0,
    carsRemoved: 0,
    energyGenerated: 0,
    carbonPowered: 0,
    savingsGenerated: 0,
  });

  const targetValues = {
    co2Reduced: CO2_TOTAL,
    treesEquivalent: TOTAL_ARBOLES,
    carsRemoved: CARS_OFF_TOTAL,
    energyGenerated: ENERGIA_TOTAL,
    carbonPowered: CARBON_TOTAL,
    savingsGenerated: AHORRO_TOTAL,
  };

  useEffect(() => {
    const duration = 2000; // 2 seconds
    const steps = 60;
    const stepDuration = duration / steps;

    let currentStep = 0;
    const timer = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;

      setAnimatedValues({
        co2Reduced: Math.floor(targetValues?.co2Reduced * progress),
        treesEquivalent: Math.floor(targetValues?.treesEquivalent * progress),
        carsRemoved: Math.floor(targetValues?.carsRemoved * progress),
        energyGenerated: Math.floor(targetValues?.energyGenerated * progress),
        carbonPowered: Math.floor(targetValues?.carbonPowered * progress),
        savingsGenerated: Math.floor(targetValues?.savingsGenerated * progress),
      });

      if (currentStep >= steps) {
        clearInterval(timer);
        setAnimatedValues(targetValues);
      }
    }, stepDuration);

    return () => clearInterval(timer);
  }, [
    CO2_TOTAL,
    TOTAL_ARBOLES,
    CARS_OFF_TOTAL,
    ENERGIA_TOTAL,
    CARBON_TOTAL,
    AHORRO_TOTAL,
  ]);

  const impactMetrics = [
    {
      id: 1,
      title: "CO₂ Reducido",
      value: roundTo(animatedValues?.co2Reduced),
      unit: "Toneladas",
      description: "Emisiones evitadas este año",
      icon: "Leaf",
      color: "success",
      trend: "+15%",
      comparison: "Impacto ambiental",
    },
    {
      id: 2,
      title: "Equivalente en Árboles",
      value: animatedValues?.treesEquivalent,
      unit: "Árboles",
      description: "Árboles plantados equivalentes",
      icon: "Trees",
      color: "secondary",
      trend: "+22%",
      comparison: "Impacto ambiental",
    },
    {
      id: 3,
      title: "Autos Fuera de Circulación",
      value: animatedValues?.carsRemoved,
      unit: "Vehículos",
      description: "Equivalente en reducción de emisiones",
      icon: "Car",
      color: "primary",
      trend: "+18%",
      comparison: "Impacto ambiental",
    },
    {
      id: 4,
      title: "Energía Generada",
      value: animatedValues?.energyGenerated,
      unit: "Wh",
      description: "Energía limpia producida",
      icon: "Zap",
      color: "warning",
      trend: "+35%",
      comparison: "Capacidad instalada",
    },
    {
      id: 5,
      title: "Carbono Reducido",
      value: animatedValues?.carbonPowered,
      unit: "Toneladas",
      description: "Carbono reducido este año",
      icon: "Building",
      color: "accent",
      trend: "+28%",
      comparison: "Impacto ambiental",
    },
    {
      id: 6,
      title: "Ahorros Generados",
      value: animatedValues?.savingsGenerated,
      unit: "GTQ",
      description: "Ahorros acumulados de nuestros clientes",
      icon: "DollarSign",
      color: "success",
      trend: "+42%",
      comparison: "Beneficio económico",
    },
  ];

  // Get data for selected year and calculate CO2/Trees dynamically
  const rawMonthlyData = getDataByYear(selectedYear);
  const processedMonthlyData = rawMonthlyData.map((d) => ({
    ...d,
    // Formula: energy (kWh) * CO2 Factor
    co2: d.energy * (FACT_CO2_PROMEDIO_ANUAL || 0),
    trees:
      d.energy *
      (FACT_CO2_PROMEDIO_ANUAL || 0) *
      (FACT_ARBOLES_PROM_ANUAL || 0),
  }));

  // Filter out months without data to avoid "zero drops" in the chart
  const chartData = processedMonthlyData.filter((d) => d.energy > 0);

  // Calculate Yearly Totals for the summary section
  const yearlyTotals = chartData.reduce(
    (acc, curr) => ({
      energy: acc.energy + curr.energy,
      co2: acc.co2 + curr.co2,
      trees: acc.trees + curr.trees,
      savings: acc.savings + curr.savings,
    }),
    { energy: 0, co2: 0, trees: 0, savings: 0 }
  );

  const ChartTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const energyData = payload.find((p) => p.dataKey === "energy");
      const co2Data = payload.find((p) => p.dataKey === "co2");
      const savingsData = payload.find((p) => p.dataKey === "savings");
      const treesData = payload.find((p) => p.dataKey === "trees");

      return (
        <div className="bg-white border border-gray-100 p-4 rounded-xl shadow-xl text-sm min-w-[200px]">
          <p className="font-bold text-gray-800 mb-2 border-b pb-1">{`Impacto en ${label} ${selectedYear}`}</p>
          <div className="space-y-1">
            <div className="flex justify-between items-center text-amber-600 font-medium">
              <span>Energía:</span>
              <span>{formatNumMil(energyData?.value * 1000 || 0)}Wh</span>
            </div>
            <div className="flex justify-between items-center text-green-600 font-medium">
              <span>CO₂ Reducido:</span>
              <span>{formatNumKgT(co2Data?.value || 0)}</span>
            </div>
            <div className="flex justify-between items-center text-emerald-700 font-medium">
              <span>Árboles:</span>
              <span>{formatNum(treesData?.value || 0, 0)}</span>
            </div>
            {showSavings && (
              <div className="flex justify-between items-center text-blue-600 font-medium">
                <span>Ahorros:</span>
                <span>{formatNumberQTZ(savingsData?.value || 0)}</span>
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  const handleYearChange = (direction) => {
    const currentIndex = availableYears.indexOf(selectedYear);
    if (direction === "prev" && currentIndex < availableYears.length - 1) {
      setSelectedYear(availableYears[currentIndex + 1]);
    } else if (direction === "next" && currentIndex > 0) {
      setSelectedYear(availableYears[currentIndex - 1]);
    }
  };

  const renderSavingsElement = () => {
    if (showSavings) {
      return (
        <Line
          yAxisId="savings"
          dataKey="savings"
          type="monotone"
          stroke="#3b82f6"
          strokeWidth={3}
          dot={{ fill: "#3b82f6", strokeWidth: 2, r: 3 }}
          activeDot={{ r: 8, fill: "#1d4ed8" }}
          name="Ahorros (GTQ)"
        />
      );
    }
    return null;
  };

  return (
    <section className="py-20 bg-gradient-to-br from-background to-muted">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-success/10 rounded-full px-4 py-2 mb-6">
            <Icon name="BarChart3" size={20} color="var(--color-success)" />
            <span className="text-success font-semibold">
              Rendimiento e Impacto
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-text-primary mb-6">
            Impacto Ambiental y Rendimiento
            <span className="solar-text-gradient block">del Sistema</span>
          </h2>
          <p className="text-xl text-text-secondary max-w-3xl mx-auto">
            Resultados medibles de la transición hacia una matriz energética
            limpia y sostenible.
          </p>
        </div>

        {/* Impact Metrics Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {impactMetrics?.map((metric) => (
            <div
              key={metric?.id}
              className="bg-white rounded-2xl p-6 solar-shadow-card solar-hover-lift"
            >
              <div className="flex items-start justify-between mb-4">
                <div
                  className={`w-12 h-12 rounded-full flex items-center justify-center ${metric?.color === "success"
                    ? "bg-success/10"
                    : metric?.color === "secondary"
                      ? "bg-secondary/10"
                      : metric?.color === "primary"
                        ? "bg-primary/10"
                        : metric?.color === "warning"
                          ? "bg-warning/10"
                          : metric?.color === "accent"
                            ? "bg-accent/10"
                            : "bg-success/10"
                    }`}
                >
                  <Icon
                    name={metric?.icon}
                    size={24}
                    color={`var(--color-${metric?.color})`}
                  />
                </div>
                {/*<div
                  className={`px-2 py-1 rounded-full text-xs font-semibold ${metric?.color === "success"
                    ? "bg-success/10 text-success"
                    : metric?.color === "secondary"
                      ? "bg-secondary/10 text-secondary"
                      : metric?.color === "primary"
                        ? "bg-primary/10 text-primary"
                        : metric?.color === "warning"
                          ? "bg-warning/10 text-warning"
                          : metric?.color === "accent"
                            ? "bg-accent/10 text-accent"
                            : "bg-success/10 text-success"
                    }`}
                >
                  {metric?.trend}
                </div>*/}
              </div>

              <div className="mb-4">
                <div className="text-3xl font-bold text-text-primary mb-1">
                  {metric?.id === 6
                    ? formatNumMil(metric?.value)
                    : formatNumber(metric?.value)}
                  <span className="text-lg text-text-secondary ml-1">
                    {metric?.unit}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-text-primary mb-1">
                  {metric?.title}
                </h3>
                <p className="text-text-secondary text-sm">
                  {metric?.description}
                </p>
              </div>

              <div className="text-xs text-text-secondary">
                {metric?.comparison}
              </div>
            </div>
          ))}
        </div>

        {/* Monthly Progress Chart */}
        {!loadingData && hasData && (
          <div className="space-y-8 mb-16">
            <div className="bg-white rounded-3xl p-8 solar-shadow-card">
              <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-6">
                <div>
                  <h3 className="text-2xl font-bold text-text-primary mb-2">
                    Progreso Mensual {selectedYear}
                  </h3>
                  <p className="text-text-secondary">
                    Evolución histórica de generación y CO₂ reducido
                  </p>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                  {/* Year Selection Group */}
                  {availableYears.length > 1 && (
                    <div className="flex items-center bg-gray-100 rounded-lg p-1 border border-gray-200">
                      <button
                        onClick={() => handleYearChange("prev")}
                        disabled={
                          availableYears.indexOf(selectedYear) ===
                          availableYears.length - 1
                        }
                        className="p-1 px-3 rounded hover:bg-white disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                        title="Año Anterior"
                      >
                        <Icon name="ChevronLeft" size={18} />
                      </button>
                      <span className="px-4 font-bold text-gray-700 min-w-[70px] text-center">
                        {selectedYear}
                      </span>
                      <button
                        onClick={() => handleYearChange("next")}
                        disabled={availableYears.indexOf(selectedYear) === 0}
                        className="p-1 px-3 rounded hover:bg-white disabled:opacity-30 disabled:hover:bg-transparent transition-all"
                        title="Año Siguiente"
                      >
                        <Icon name="ChevronRight" size={18} />
                      </button>
                    </div>
                  )}

                  {/*<div className="h-8 w-[1px] bg-gray-200 hidden md:block"></div> 

                 <button
                    onClick={() => setShowSavings(!showSavings)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all border ${showSavings
                      ? "bg-primary/10 border-primary/20 text-primary font-semibold"
                      : "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                      }`}
                  >
                    <Icon name={showSavings ? "Eye" : "EyeOff"} size={18} />
                    {showSavings ? "Ocultar Ahorro" : "Mostrar Ahorro"}
                  </button>*/}
                </div>
              </div>

              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={chartData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="month"
                      tick={{ fontSize: 12 }}
                      axisLine={{ stroke: "#d1d5db" }}
                    />
                    <YAxis
                      yAxisId="left"
                      orientation="left"
                      stroke="#f59e0b"
                      tick={{ fontSize: 12 }}
                      label={{
                        value: "Energía",
                        angle: -90,
                        position: "insideLeft",
                      }}
                    />
                    <YAxis
                      yAxisId="right"
                      orientation="right"
                      stroke="#10b981"
                      tick={{ fontSize: 12 }}
                      label={{
                        value: "Impacto (t / Árboles)",
                        angle: 90,
                        position: "insideRight",
                      }}
                    />
                    {showSavings && (
                      <YAxis
                        yAxisId="savings"
                        orientation="right"
                        stroke="#3b82f6"
                        tick={false}
                        axisLine={false}
                        tickLine={false}
                        hide
                      />
                    )}
                    <Tooltip content={<ChartTooltip />} />
                    <Legend verticalAlign="top" height={36} />
                    <Bar
                      yAxisId="left"
                      dataKey="energy"
                      fill="#f59e0b"
                      name="Energía (KWh)"
                      radius={[4, 4, 0, 0]}
                    />
                    <Line
                      yAxisId="right"
                      dataKey="co2"
                      type="monotone"
                      stroke="#10b981"
                      strokeWidth={3}
                      dot={{ fill: "#10b981", strokeWidth: 2, r: 3 }}
                      activeDot={{ r: 6, fill: "#059669" }}
                      name="CO₂ Reducido (t)"
                    />
                    <Line
                      yAxisId="right"
                      dataKey="trees"
                      type="monotone"
                      stroke="#059669"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={{ fill: "#059669", strokeWidth: 1, r: 2 }}
                      name="Árboles Eq."
                    />
                    {renderSavingsElement()}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Yearly Summary Report Section */}
            <div className="bg-primary/5 border border-primary/10 rounded-3xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-primary/10 rounded-lg text-primary">
                  <Icon name="Calendar" size={24} />
                </div>
                <h3 className="text-xl font-bold text-text-primary">
                  Resumen Acumulado del Año {selectedYear}
                </h3>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                <div className="space-y-1">
                  <p className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest text-primary/70">
                    Energía Total
                  </p>
                  <p className="text-2xl font-bold text-foreground">
                    {formatNumMil(yearlyTotals.energy * 1000)}
                    <span className="text-sm font-medium ml-1 text-muted-foreground">
                      Wh
                    </span>
                  </p>
                </div>

                <div className="space-y-1">
                  <p className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest text-success/70">
                    CO₂ Reducido
                  </p>
                  <p className="text-2xl font-bold text-foreground">
                    {formatNumKgT(yearlyTotals.co2)}
                  </p>
                </div>

                <div className="space-y-1">
                  <p className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest text-emerald-700/70">
                    Arboles Eq.
                  </p>
                  <p className="text-2xl font-bold text-foreground">
                    {formatNum(yearlyTotals.trees, 0)}
                    <span className="text-sm font-medium ml-1 text-muted-foreground">
                      Árboles
                    </span>
                  </p>
                </div>

                <div className="space-y-1">
                  <p className="text-[10px] uppercase font-bold text-muted-foreground tracking-widest text-blue-600/70">
                    Ahorro Total
                  </p>
                  <p className="text-2xl font-bold text-foreground text-blue-600">
                    {formatNumberQTZ(yearlyTotals.savings)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ImpactDashboard;
